// ============================================
// APPLICATION - Feature Flags & Granel Hooks
// ============================================

import { useQuery } from '@tanstack/react-query';
import { featureFlagsRepository, granelRepository } from '../../infrastructure/http/feature-flags.repository';
import { queryKeys } from '../query-keys';
import { useAuthStore } from '../../state/auth.store';

// Hook para verificar si un feature flag está habilitado
export const useFeatureFlag = (feature: string) => {
  const accessToken = useAuthStore((state) => state.accessToken);

  return useQuery({
    queryKey: queryKeys.featureFlags.check(feature),
    queryFn: () => featureFlagsRepository.checkFeature(feature),
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: 1,
    throwOnError: false,
  });
};

// Hook para obtener categorías de granel
export const useGranelCategorias = (enabled: boolean = true) => {
  const accessToken = useAuthStore((state) => state.accessToken);

  return useQuery({
    queryKey: queryKeys.granel.categorias(),
    queryFn: () => granelRepository.getCategorias(),
    enabled: !!accessToken && enabled,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 15 * 60 * 1000, // 15 minutos
    retry: 1,
    throwOnError: false,
  });
};

// Hook para obtener tipos de granel según categoría
export const useGranelTipos = (categoriaId: number | null, enabled: boolean = true) => {
  const accessToken = useAuthStore((state) => state.accessToken);

  return useQuery({
    queryKey: queryKeys.granel.tipos(categoriaId),
    queryFn: () => granelRepository.getTipos(categoriaId!),
    enabled: !!accessToken && enabled && categoriaId !== null,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 15 * 60 * 1000, // 15 minutos
    retry: 1,
    throwOnError: false,
  });
};

// Hook para obtener los productos estándar (marca + código de barra) de un tipo de granel
export const useGranelProductosEstandar = (tipoId: number | null, enabled: boolean = true) => {
  const accessToken = useAuthStore((state) => state.accessToken);

  return useQuery({
    queryKey: queryKeys.granel.productosEstandar(tipoId),
    queryFn: () => granelRepository.getProductosEstandar(tipoId!),
    enabled: !!accessToken && enabled && tipoId !== null,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 15 * 60 * 1000, // 15 minutos
    retry: 1,
    throwOnError: false,
  });
};

// Hook para obtener todos los productos granel
export const useProductosGranel = (enabled: boolean = true) => {
  const accessToken = useAuthStore((state) => state.accessToken);

  return useQuery({
    queryKey: queryKeys.granel.productos(),
    queryFn: () => granelRepository.getProductos(),
    enabled: !!accessToken && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: 1,
    throwOnError: false,
  });
};
